import LeitorDePdf from "../componentes/LeitorDePdf.jsx";
import { useEffect, useState } from "react";
import { Botao, Cortina, Etiqueta } from "../componentes/comuns.jsx";
import { useAcoes, useCasoAtual, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { nomeDoArquivo, urlDoDocumento } from "../conversa.js";
import { familiaDoAnexo } from "../conversa.js";
import { ICONES, Icone, dataHora } from "../visual.jsx";

/**
 * Conferir os documentos do cliente — um por um, com o documento na tela.
 *
 * O que existia era uma lista de nomes com uma pastilha do lado: para decidir
 * se o holerite serve, a pessoa tinha de abrir o acervo em outra tela, achar o
 * arquivo, voltar e clicar num menu de quatro palavras. A conferência é a ação
 * mais repetida da mesa — quatro por caso, todo dia — e era a menos assistida
 * de todas.
 *
 * Aqui é uma coisa só: o documento grande à esquerda, o que o sistema já sabe
 * dele à direita (o que a IA classificou e com quanta confiança), e três
 * decisões de dedo — aprovar, ilegível, dispensar. Ao decidir, anda sozinho
 * para o próximo; no fim, diz que acabou e some.
 *
 * A decisão continua sendo a mesma rota de sempre (`conferirDocumento`), com a
 * mesma permissão e a mesma trilha: isto é desenho, não caminho novo.
 */

/* Os dois selos do cabeçalho. Ficam aqui, e não soltos no meio do JSX, porque
   `Etiqueta` recebe o par [fundo, letra] em VALOR — é o único lugar da casa onde
   cor não cabe em classe. São as mesmas duas de `CORES_DO_ESTADO`, na ficha. */
const CORES = {
  prontos: ["color-mix(in oklab, var(--color-verde-fundo) 14%, transparent)", "var(--color-verde-fundo)"],
  esperando: ["color-mix(in oklab, var(--color-ambar) 16%, transparent)", "var(--color-ambar-escuro)"],
};

const DECISOES = [
  {
    estado: "aprovado",
    rotulo: "Aprovar",
    dica: "Serve para a peça — segue para o checklist aprovado",
    destaque: true,
  },
  {
    estado: "ilegivel",
    rotulo: "Ilegível",
    dica: "Volta para o cliente: a foto não dá para ler",
  },
  {
    estado: "dispensado",
    rotulo: "Dispensar",
    dica: "Este caso não precisa deste documento",
  },
];

/** O quadro do documento: imagem aparece, PDF abre em quadro, o resto se baixa. */
function Prevendo({ url, nome }) {
  const familia = familiaDoAnexo(nome);
  if (!url)
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-corpo text-muted-foreground">
        O arquivo não está na pasta do cliente. Conferir sem ver o documento é assinar embaixo do que não se leu — peça o reenvio.
      </div>
    );
  if (familia === "imagem")
    return <img src={url} alt={nome} className="mx-auto max-h-full max-w-full rounded-lg object-contain" />;
  if (familia === "pdf")
    return <LeitorDePdf url={url} nome={nome} parametros="toolbar=0&navpanes=0&view=FitH" className="size-full rounded-lg" />;
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      {/* `ICONES.arquivo` NÃO existe: o componente de ícone faz spread do
          desenho e um `undefined` derruba a tela inteira com "is not iterable".
          O nome certo é `documento`. `visual.test.jsx` passou a reprovar nome
          fantasma — este ficou vivo até 27/08 sem ninguém notar. */}
      <Icone de={ICONES.documento} tamanho={34} cor="var(--color-tinta-fantasma)" />
      <div className="text-corpo text-tinta-suave">{nome}</div>
      <a href={url} target="_blank" rel="noreferrer" className="text-corpo font-bold">
        Abrir o arquivo
      </a>
    </div>
  );
}

/**
 * A mesa de conferir em si — o documento grande e as três decisões.
 *
 * Dois lugares a abrem: o caso aberto (a fila é o checklist dele) e Documentos,
 * com a fila de TODOS os casos (`api.filaAConferir`). O item pode trazer o
 * próprio cliente, a pasta e a conta do caso dele: é o que deixa a mesma mesa
 * passar de um caso para o outro sem reabrir nada — as teclas 1/2/3 continuam
 * as mesmas, e o nome no alto troca quando o caso troca.
 */
export function MesaDeConferencia({ fila, cliente, pasta, total, prontos, aoDecidir, aoFechar, vazio }) {
  const [i, setI] = useState(0);
  const [decidindo, setDecidindo] = useState(false);

  /* A fila encolhe a cada decisão — o item conferido sai quando quem chama
     recarrega —, então o índice anda sozinho e não precisa de conta. */
  const doc = fila[Math.min(i, Math.max(0, fila.length - 1))];
  const nomeDoCliente = doc?.cliente ?? cliente;
  const pastaDoDoc = doc?.pasta ?? pasta;
  const totalDoCaso = doc?.total ?? total;
  const prontosDoCaso = doc?.prontos ?? prontos;

  async function decidir(estado) {
    if (!doc || decidindo) return;
    setDecidindo(true);
    try {
      await aoDecidir(doc, estado);
    } finally {
      setDecidindo(false);
    }
  }

  /* Teclado: quem confere quarenta documentos numa tarde não quer o mouse.
     1/2/3 decidem, setas andam, Esc fecha (a `Cortina` cuida do Esc). */
  useEffect(() => {
    const tecla = (e) => {
      if (decidindo || !doc) return;
      const n = ["1", "2", "3"].indexOf(e.key);
      if (n >= 0) {
        e.preventDefault();
        void decidir(DECISOES[n].estado);
      }
      if (e.key === "ArrowRight") setI((v) => Math.min(v + 1, fila.length - 1));
      if (e.key === "ArrowLeft") setI((v) => Math.max(v - 1, 0));
    };
    addEventListener("keydown", tecla);
    return () => removeEventListener("keydown", tecla);
  });

  return (
    <Cortina
      aoFechar={aoFechar}
      largura="min(1080px, 94vw)"
      titulo="Conferir os documentos"
      antesDoTitulo={<Icone de={ICONES.files} tamanho={19} />}
      acima={
        <span className="flex flex-wrap items-center gap-2.5">
          {nomeDoCliente ? <span>{nomeDoCliente}</span> : null}
          {totalDoCaso ? (
            <Etiqueta cores={CORES.prontos}>
              {prontosDoCaso} de {totalDoCaso} prontos
            </Etiqueta>
          ) : null}
          {fila.length ? <Etiqueta cores={CORES.esperando}>{fila.length} esperando você</Etiqueta> : null}
        </span>
      }
      rodape={
        doc ? (
          <div className="flex w-full flex-wrap items-center gap-2">
            {DECISOES.map((d, n) => (
              <Botao
                key={d.estado}
                rotulo={`${d.rotulo}  ${n + 1}`}
                destaque={d.destaque}
                desabilitado={decidindo}
                titulo={d.dica}
                aoTocar={() => void decidir(d.estado)}
              />
            ))}
            <span className="ml-auto font-mono text-selo text-muted-foreground">
              1 aprova · 2 ilegível · 3 dispensa · ← → anda
            </span>
          </div>
        ) : (
          <Botao rotulo="Fechar" destaque aoTocar={aoFechar} />
        )
      }
    >
      {doc ? (
        <div className="flex min-h-[380px] flex-col gap-4 lg:flex-row">
          {/* O documento, grande. É o que decide, e por isso ocupa a tela. */}
          <div className="min-h-[300px] flex-1 overflow-hidden rounded-xl border border-border bg-papel-fundo p-2">
            <Prevendo url={urlDoDocumento(pastaDoDoc, doc.arquivo)} nome={nomeDoArquivo(doc.arquivo) || doc.documento} />
          </div>

          <div className="flex w-full flex-none flex-col gap-3 lg:w-[300px]">
            <div>
              <div className="font-mono text-selo tracking-[.08em] text-muted-foreground">
                DOCUMENTO {Math.min(i + 1, fila.length)} DE {fila.length}
              </div>
              <div className="mt-1 font-serif text-[22px] leading-[1.2]">{doc.documento}</div>
              {doc.arquivo ? (
                <div className="mt-1 font-mono text-selo break-all text-muted-foreground">{nomeDoArquivo(doc.arquivo)}</div>
              ) : null}
            </div>

            {/* O que a máquina já disse. Ela CLASSIFICA e não decide: o número da
                confiança está aqui para a pessoa saber quanto desconfiar. */}
            <div className="rounded-xl border border-border bg-card p-[12px_13px]">
              <div className="font-mono text-selo tracking-[.08em] text-muted-foreground">AVALIAÇÃO AUTOMÁTICA</div>
              <div className="mt-1.5 text-corpo leading-[1.5]">
                {doc.origem === "copiloto" || doc.confianca != null ? (
                  <>
                    O sistema leu o arquivo e classificou como <strong>{doc.documento}</strong>
                    {doc.confianca != null ? (
                      <>
                        , com <strong>{Math.round(Number(doc.confianca) * 100)}%</strong> de confiança
                      </>
                    ) : null}
                    .
                  </>
                ) : doc.origem === "assinatura" ? (
                  <>Fotografado pelo cliente na tela de assinatura do contrato.</>
                ) : (
                  <>Chegou pelo WhatsApp e foi guardado na pasta do cliente. A classificação é sua.</>
                )}
              </div>
              {doc.recebido_em ? (
                <div className="mt-2 font-mono text-selo text-muted-foreground">recebido em {dataHora(doc.recebido_em)}</div>
              ) : null}
            </div>

            <div className="rounded-xl border border-dashed border-border p-[12px_13px] text-apoio leading-[1.55] text-tinta-suave">
              Aprovar põe o documento como prova da peça. <strong>Ilegível</strong> devolve para o cliente e volta para a régua de
              cobrança. <strong>Dispensar</strong> tira do checklist deste caso — e é o único dos três que não pede nada de
              ninguém.
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 text-center">
          <Icone de={ICONES.ok ?? ICONES.files} tamanho={30} cor="var(--color-verde-fundo)" />
          <div className="font-serif text-[22px]">Nada esperando conferência</div>
          <div className="max-w-[420px] text-corpo leading-[1.55] text-muted-foreground">{vazio}</div>
        </div>
      )}
    </Cortina>
  );
}

export default function ConferirDocumentos() {
  const { caso } = useEstado();
  const atual = useCasoAtual();
  const { conferirDocumento } = useAcoes();
  const { ajustar } = useNavegacao();

  if (!caso || !atual) return null;

  const itens = caso.checklist ?? [];
  const total = itens.length;
  const prontos = itens.filter((d) => d.estado === "aprovado" || d.estado === "dispensado").length;

  return (
    <MesaDeConferencia
      fila={itens.filter((d) => d.estado === "recebido")}
      cliente={atual.nome}
      pasta={caso.pasta}
      total={total}
      prontos={prontos}
      aoDecidir={(doc, estado) => conferirDocumento(doc.id, estado)}
      aoFechar={() => ajustar({ conferir: null })}
      vazio={
        prontos === total && total > 0
          ? "Os documentos deste caso estão todos conferidos — o último conferido já abriu o portão do manual."
          : "Quando o cliente mandar um documento pelo WhatsApp, ele aparece aqui para você dizer se serve."
      }
    />
  );
}
